import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Polls `GET /api/payments/transfers/alerts/count` for the current month and
 * returns `{ count, hasAlert, loading }`. Intended for the Sidebar badge that
 * warns operators about retryable transfers without requiring them to open
 * the Pagamentos tab.
 *
 * - Respects the caller's company scope (server-side permission `pagamentos`).
 * - Polls every `intervalMs` (default 60s) and on window focus.
 * - Silently returns 0 for users without `pagamentos` permission (403 swallowed).
 */
export default function useTransferAlerts({ intervalMs = 60000, enabled = true } = {}) {
  const [count, setCount] = useState(0);
  const [cashinCount, setCashinCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.get("/payments/transfers/alerts/count");
      setCount(res.data?.count || 0);
      setCashinCount(res.data?.cashin_count || 0);
    } catch {
      setCount(0);
      setCashinCount(0);
    }
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchCount();
    const id = setInterval(fetchCount, intervalMs);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, intervalMs, fetchCount]);

  return { count, cashinCount, hasAlert: count > 0, hasCashinAlert: cashinCount > 0, loading, refetch: fetchCount };
}
